import { captureMessage } from '@sentry/vue';
import type { ActionTree } from 'vuex';

import type { ClientData } from '@api/api.model';
import { ApiService } from '@api/api.service';

import { AppGetters } from '../App/getters';
import type { BrokerData } from '../App/interfaces';
import { AppMutations } from '../App/mutations';
import type { State } from '../interfaces/state';
import { StepsMutations } from '../Steps/mutations';
import type { ActionAugments } from '../types/actions';
import { BrokerGetters } from './getters';
import type { BrokerState } from './interfaces';
import { BrokerMutations } from './mutations';

const api = new ApiService();

export enum BrokerActions {
  GetClientData = 'Broker:GET_MISSING_DATA',
  UpdateInitialData = 'Broker:UPDATE_INITIAL_DATA',
  SetData = 'Broker:SET_DATA',
  SendClientData = 'Broker:SEND_CLIENT_DATA',
  UpdateClientData = 'Broker:UPDATE_CLIENT_DATA',
}

export interface BrokerActionsData {
  [BrokerActions.GetClientData]: (context: ActionAugments, brokerData: BrokerData) => void;
  [BrokerActions.UpdateInitialData]: (context: ActionAugments, selectedCountryCode?: string) => void;
  [BrokerActions.SetData]: (context: ActionAugments, data: BrokerState) => void;
  [BrokerActions.SendClientData]: (context: ActionAugments, clientData: ClientData) => void;
  [BrokerActions.UpdateClientData]: (context: ActionAugments, payload: { prop: string; value: string }) => void;
}

export const actions: ActionTree<State, State> & BrokerActionsData = {
  async [BrokerActions.GetClientData]({ commit }, { brokerId, token }) {
    commit(AppMutations.ToggleLoading);
    commit(BrokerMutations.SetClientData, await api.getClientData(brokerId, token));
    commit(AppMutations.ToggleLoading);
  },

  async [BrokerActions.UpdateInitialData]({ dispatch, commit, getters }, selectedCountryCode) {
    const headers = getters[AppGetters.GetHeaders];

    const payload = {
      headers,
      selectedCountryCode,
    } as const;

    commit(StepsMutations.SetDataLoading, true);

    try {
      let { initialData } = await api.getUpdateInitialData(payload);

      if (selectedCountryCode) {
        initialData = { ...initialData, selectedCountry: selectedCountryCode };
      }

      dispatch(BrokerActions.SetData, initialData);
    } catch (error: unknown) {
      captureMessage(`api.getUpdateInitialData: ${error}`, { level: 'warning' });
    } finally {
      commit(StepsMutations.SetDataLoading, false);
    }
  },

  async [BrokerActions.SetData]({ commit }, data) {
    await commit(BrokerMutations.SetData, data);
  },

  async [BrokerActions.SendClientData]({ getters }) {
    const clientData = getters[BrokerGetters.GetClientData];
    return api.putClientData(getters[AppGetters.GetHeaders], { clientData });
  },

  [BrokerActions.UpdateClientData]({ commit }, { prop, value }) {
    commit(BrokerMutations.UpdateClientData, { prop, value });
  },
} as const;
